import { useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useStore } from '../store'
import { PageHeader, Card, CardTitle, GoldButton, GhostButton, colors, fonts, fmt } from '../components/UI'
import { containsSensitiveData, SENSITIVE_DATA_ERROR } from '../lib/sensitiveDataCheck'

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCOUNT_TYPES = ['current', 'savings', 'isa', 'pension']
const BILL_CATEGORIES = ['Housing', 'Utilities', 'Insurance', 'Subscriptions', 'Transport', 'Groceries', 'Health', 'Entertainment', 'Other']
const BILL_FREQUENCIES = ['monthly', 'weekly', 'quarterly', 'every_n_months', 'annual', 'one_off']

// ─── Template CSV strings ────────────────────────────────────────────────────

const ACCOUNTS_TEMPLATE = [
  'name,type,balance,is_emergency_fund,owner_label',
  'Barclays Current,current,1200,no,Oliver',
  'Nationwide Savings,savings,5000,yes,Joint',
  'Stocks & Shares ISA,isa,8000,no,Vikki',
].join('\n')

const BILLS_TEMPLATE = [
  'name,amount,due_day,category,frequency,interval_months,anchor_month,anchor_year,owner_label',
  'Rent,950,1,Housing,monthly,,,Joint',
  'Netflix,17.99,15,Subscriptions,monthly,,,Joint',
  'Council Tax,180,1,Housing,monthly,,,Joint',
  'Gym,35,1,Health,monthly,,,Vikki',
  'Car Insurance,420,6,Insurance,annual,,,6,2024,Oliver',
  'Boiler service,120,1,Housing,every_n_months,,12,9,2024,Joint',
].join('\n')

// ─── Utilities ───────────────────────────────────────────────────────────────

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim())
  if (lines.length < 2) return { headers: [], rows: [] }

  function parseLine(line) {
    const fields = []
    let field = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') {
        inQuotes = !inQuotes
      } else if (ch === ',' && !inQuotes) {
        fields.push(field.trim())
        field = ''
      } else {
        field += ch
      }
    }
    fields.push(field.trim())
    return fields
  }

  const headers = parseLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_').trim())
  const rows = lines.slice(1).map(line => {
    const values = parseLine(line)
    const row = {}
    headers.forEach((h, i) => { row[h] = (values[i] || '').trim() })
    return row
  }).filter(row => Object.values(row).some(v => v !== ''))

  return { headers, rows }
}

function detectFileType(headers) {
  if (headers.includes('due_day') || headers.includes('frequency')) return 'bills'
  if (headers.includes('balance') || headers.includes('is_emergency_fund')) return 'accounts'
  return null
}

// ─── Validation ──────────────────────────────────────────────────────────────

function validateAccountRow(row, idx) {
  const errors = []
  const name = (row.name || '').trim()

  if (!name) errors.push('Name is required')
  else if (containsSensitiveData(name)) errors.push(SENSITIVE_DATA_ERROR)

  const type = (row.type || '').toLowerCase().trim()
  if (!ACCOUNT_TYPES.includes(type)) {
    errors.push(`Type must be: ${ACCOUNT_TYPES.join(', ')}`)
  }

  const balance = parseFloat(row.balance)
  if (isNaN(balance)) errors.push('Balance must be a number')

  return {
    idx,
    name,
    type,
    balance: isNaN(balance) ? 0 : balance,
    is_emergency_fund: (row.is_emergency_fund || '').toLowerCase() === 'yes',
    owner_label: (row.owner_label || '').trim() || 'Joint',
    errors,
    valid: errors.length === 0,
  }
}

function validateBillRow(row, idx) {
  const errors = []
  const name = (row.name || '').trim()

  if (!name) errors.push('Name is required')
  else if (containsSensitiveData(name)) errors.push(SENSITIVE_DATA_ERROR)

  const amount = parseFloat(row.amount)
  if (isNaN(amount) || amount < 0) errors.push('Amount must be a positive number')

  const dueDay = parseInt(row.due_day)
  if (isNaN(dueDay) || dueDay < 1 || dueDay > 31) errors.push('Due day must be 1-31')

  const freq = (row.frequency || 'monthly').toLowerCase().trim()
  if (!BILL_FREQUENCIES.includes(freq)) {
    errors.push(`Frequency must be: ${BILL_FREQUENCIES.join(', ')}`)
  }

  let interval_months = null
  let anchor_month = null
  let anchor_year = null

  if (freq === 'every_n_months') {
    interval_months = parseInt(row.interval_months)
    if (isNaN(interval_months) || interval_months < 2) errors.push('interval_months must be 2 or more')
    anchor_month = parseInt(row.anchor_month) || 1
  } else if (freq === 'annual' || freq === 'quarterly') {
    anchor_month = parseInt(row.anchor_month)
    if (isNaN(anchor_month) || anchor_month < 1 || anchor_month > 12) {
      errors.push('anchor_month (1-12) required for annual/quarterly')
      anchor_month = null
    }
  } else if (freq === 'one_off') {
    anchor_month = parseInt(row.anchor_month)
    anchor_year = parseInt(row.anchor_year)
    if (isNaN(anchor_month) || anchor_month < 1 || anchor_month > 12) errors.push('anchor_month required for one_off')
    if (isNaN(anchor_year) || anchor_year < 2020) errors.push('anchor_year (YYYY) required for one_off')
  }

  const rawCat = (row.category || '').trim()
  const category = BILL_CATEGORIES.find(c => c.toLowerCase() === rawCat.toLowerCase()) || 'Other'

  return {
    idx,
    name,
    amount: isNaN(amount) ? 0 : amount,
    due_day: isNaN(dueDay) ? 1 : dueDay,
    category,
    frequency: freq,
    interval_months,
    anchor_month,
    anchor_year,
    owner_label: (row.owner_label || '').trim() || null,
    errors,
    valid: errors.length === 0,
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StepIndicator({ current }) {
  const steps = ['Templates', 'Upload', 'Preview', 'Done']
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '28px' }}>
      {steps.map((label, i) => {
        const num = i + 1
        const done = num < current
        const active = num === current
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? colors.green : active ? colors.gold : 'rgba(255,255,255,0.06)',
                border: `2px solid ${done ? colors.green : active ? colors.gold : 'rgba(255,255,255,0.1)'}`,
                fontSize: '14px', fontFamily: fonts.body, fontWeight: 700,
                color: done || active ? colors.onAccent : colors.muted,
                flexShrink: 0,
              }}>
                {done ? '✓' : num}
              </div>
              <span style={{ fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, letterSpacing: '0.07em', color: active ? colors.gold : done ? colors.green : colors.muted, whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: '2px', background: done ? colors.green : 'rgba(255,255,255,0.08)', margin: '0 6px', marginBottom: '20px' }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

function ValidationBadge({ valid, errorCount }) {
  if (valid) return (
    <span style={{ fontSize: '16px', fontFamily: fonts.body, fontWeight: 600, color: colors.green, background: 'rgba(61,184,122,0.1)', padding: '2px 8px', borderRadius: '10px' }}>✓ OK</span>
  )
  return (
    <span style={{ fontSize: '16px', fontFamily: fonts.body, fontWeight: 600, color: colors.red, background: 'rgba(224,82,82,0.1)', padding: '2px 8px', borderRadius: '10px' }}>{errorCount} error{errorCount !== 1 ? 's' : ''}</span>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function ImportPage({ onNavigate, defaultTab }) {
  const { user } = useAuth()
  const fetchAccounts = useStore(s => s.fetchAccounts)
  const fetchBills = useStore(s => s.fetchBills)

  const [step, setStep] = useState(1)
  const [fileType, setFileType] = useState(null)   // 'accounts' | 'bills'
  const [parsed, setParsed] = useState([])          // validated row objects
  const [parseError, setParseError] = useState('')
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState({ imported: 0, skipped: 0 })
  const fileRef = useRef(null)

  // ── Step 1: Templates ──────────────────────────────────────────────────────

  function renderTemplates() {
    return (
      <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '12px' }}>STEP 1 - DOWNLOAD A TEMPLATE</div>
        <p style={{ fontSize: '16px', color: colors.muted, fontFamily: fonts.body, lineHeight: 1.7, marginBottom: '20px' }}>
          Download the template for what you want to import, fill it in with your data, then come back to upload it. Import accounts and bills separately - one file at a time.
        </p>
        {defaultTab === 'bills' && (
          <div style={{ background: 'rgba(201,151,43,0.12)', border: '1px solid rgba(201,151,43,0.4)', borderRadius: '10px', padding: '12px 14px', marginBottom: '20px', fontSize: '14px', fontFamily: fonts.body, color: colors.text, lineHeight: 1.6 }}>
            You're importing bills. Grab the <strong>Bills</strong> template below, fill it in, then upload it.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
          {[
            {
              title: 'Accounts',
              desc: 'Current, savings, ISA, pension accounts',
              file: 'tic-accounts-template.csv',
              content: ACCOUNTS_TEMPLATE,
              cols: ['name', 'type', 'balance', 'is_emergency_fund', 'owner_label'],
            },
            {
              title: 'Bills',
              desc: 'Recurring bills with frequency scheduling',
              file: 'tic-bills-template.csv',
              content: BILLS_TEMPLATE,
              cols: ['name', 'amount', 'due_day', 'category', 'frequency', '…'],
            },
          ].map(t => { const focused = defaultTab && t.title.toLowerCase() === defaultTab; return (
            <div key={t.title} style={{ position: 'relative', background: 'rgba(255,255,255,0.03)', border: focused ? '1px solid rgba(201,151,43,0.6)' : '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '18px', boxShadow: focused ? '0 0 0 3px rgba(201,151,43,0.15)' : 'none' }}>
              {focused && (<span style={{ position: 'absolute', top: '-9px', left: '14px', background: colors.gold, color: colors.onAccent, fontFamily: fonts.body, fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', padding: '2px 8px', borderRadius: '10px' }}>START HERE</span>)}
              <div style={{ fontFamily: fonts.heading, fontSize: '16px', fontWeight: 700, color: colors.gold, marginBottom: '4px' }}>{t.title}</div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginBottom: '12px' }}>{t.desc}</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
                {t.cols.map(c => (
                  <span key={c} style={{ fontSize: '14px', fontFamily: fonts.mono, color: colors.muted, background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: '10px' }}>{c}</span>
                ))}
              </div>
              <GoldButton small onClick={() => downloadCSV(t.content, t.file)}>
                ↓ Download {t.title} template
              </GoldButton>
            </div>
          ) })}
        </div>

        {/* Column guide */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ fontSize: '16px', fontFamily: fonts.body, fontWeight: 600, color: colors.gold, letterSpacing: '0.1em', marginBottom: '12px' }}>Column guide</div>
          <div style={{ display: 'grid', gap: '8px' }}>
            {[
              ['type (accounts)', 'current · savings · isa · pension'],
              ['is_emergency_fund', 'yes · no'],
              ['frequency (bills)', 'monthly · weekly · quarterly · annual · every_n_months · one_off'],
              ['interval_months', 'Number ≥ 2 - only for every_n_months (e.g. 3 = every quarter from anchor)'],
              ['anchor_month', '1-12 - required for annual, quarterly, every_n_months, one_off'],
              ['anchor_year', 'YYYY - required for one_off only'],
              ['owner_label', 'Person name or Joint - leave blank to default to Joint'],
              ['category (bills)', 'Housing · Utilities · Insurance · Subscriptions · Transport · Groceries · Health · Entertainment · Other'],
            ].map(([col, vals]) => (
              <div key={col} style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '12px', fontSize: '14px', fontFamily: fonts.body }}>
                <span style={{ fontFamily: fonts.mono, color: colors.gold }}>{col}</span>
                <span style={{ color: colors.muted }}>{vals}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <GoldButton onClick={() => setStep(2)}>I've filled in my template →</GoldButton>
        </div>
      </div>
    )
  }

  // ── Step 2: Upload ─────────────────────────────────────────────────────────

  function handleFile(file) {
    if (!file) return
    if (!file.name.endsWith('.csv')) { setParseError('Please upload a CSV file (.csv)'); return }
    setParseError('')

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target.result
      const { headers, rows } = parseCSV(text)

      if (headers.length === 0 || rows.length === 0) {
        setParseError('The file appears to be empty or has no data rows.')
        return
      }

      const type = detectFileType(headers)
      if (!type) {
        setParseError("Couldn't identify this as an accounts or bills template. Make sure you're using the downloaded template and haven't renamed the column headers.")
        return
      }

      const validated = type === 'accounts'
        ? rows.map((r, i) => validateAccountRow(r, i))
        : rows.map((r, i) => validateBillRow(r, i))

      setFileType(type)
      setParsed(validated)
      setStep(3)
    }
    reader.readAsText(file)
  }

  function renderUpload() {
    return (
      <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '12px' }}>STEP 2 - UPLOAD YOUR FILLED TEMPLATE</div>
        <p style={{ fontSize: '16px', color: colors.muted, fontFamily: fonts.body, lineHeight: 1.7, marginBottom: '20px' }}>
          Upload the CSV file you filled in. The tracker will detect whether it's an accounts or bills file automatically.
        </p>

        <div
          style={{
            border: `2px dashed ${colors.borderGold}`,
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            marginBottom: '16px',
            background: 'rgba(201,151,43,0.03)',
          }}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]) }}
        >
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📂</div>
          <div style={{ fontFamily: fonts.heading, fontSize: '16px', color: colors.text, marginBottom: '6px' }}>
            Click to choose file or drag and drop
          </div>
          <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body }}>CSV files only</div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
        </div>

        {parseError && (
          <div style={{ fontSize: '14px', color: colors.red, fontFamily: fonts.body, padding: '10px 14px', background: 'rgba(224,82,82,0.1)', borderRadius: '10px', marginBottom: '16px' }}>
            {parseError}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <GhostButton onClick={() => setStep(1)}>← Back</GhostButton>
        </div>
      </div>
    )
  }

  // ── Step 3: Preview ────────────────────────────────────────────────────────

  const validRows = parsed.filter(r => r.valid)
  const invalidRows = parsed.filter(r => !r.valid)

  async function handleImport() {
    if (validRows.length === 0) return
    setImporting(true)

    try {
      if (fileType === 'accounts') {
        const payload = validRows.map(r => ({
          user_id: user.id,
          name: r.name,
          type: r.type,
          balance: r.balance,
          is_emergency_fund: r.is_emergency_fund,
          owner_label: r.owner_label,
        }))
        await supabase.from('accounts').insert(payload)
        await fetchAccounts()
      } else {
        const payload = validRows.map(r => ({
          user_id: user.id,
          name: r.name,
          amount: r.amount,
          due_day: r.due_day,
          category: r.category,
          frequency: r.frequency,
          interval_months: r.interval_months,
          anchor_month: r.anchor_month,
          anchor_year: r.anchor_year,
          owner_label: r.owner_label,
        }))
        await supabase.from('bills').insert(payload)
        await fetchBills()
      }

      setImportResult({ imported: validRows.length, skipped: invalidRows.length })
      setStep(4)
    } catch (err) {
      console.error('Import error:', err)
    }

    setImporting(false)
  }

  function renderPreview() {
    const isAccounts = fileType === 'accounts'

    return (
      <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <CardTitle style={{ margin: 0 }}>Step 3 - Review before importing</CardTitle>
          <div style={{ display: 'flex', gap: '10px' }}>
            <span style={{ fontSize: '14px', fontFamily: fonts.body, color: colors.green, background: 'rgba(61,184,122,0.1)', padding: '4px 10px', borderRadius: '10px', fontWeight: 600 }}>
              {validRows.length} ready
            </span>
            {invalidRows.length > 0 && (
              <span style={{ fontSize: '14px', fontFamily: fonts.body, color: colors.red, background: 'rgba(224,82,82,0.1)', padding: '4px 10px', borderRadius: '10px', fontWeight: 600 }}>
                {invalidRows.length} will be skipped
              </span>
            )}
          </div>
        </div>

        {invalidRows.length > 0 && (
          <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, padding: '10px 14px', background: 'rgba(224,82,82,0.06)', border: '1px solid rgba(224,82,82,0.2)', borderRadius: '10px', marginBottom: '16px', lineHeight: 1.6 }}>
            Rows with errors will be skipped. Fix them in your CSV and re-upload to import them, or continue to import the valid rows now.
          </div>
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', fontFamily: fonts.body }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.borderGold}` }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Name</th>
                {isAccounts ? (
                  <>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Balance</th>
                    <th style={thStyle}>Emergency fund</th>
                    <th style={thStyle}>Owner</th>
                  </>
                ) : (
                  <>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Due</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Frequency</th>
                    <th style={thStyle}>Owner</th>
                  </>
                )}
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {parsed.map(row => (
                <tr
                  key={row.idx}
                  style={{
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    background: row.valid ? 'transparent' : 'rgba(224,82,82,0.04)',
                    opacity: row.valid ? 1 : 0.7,
                  }}
                >
                  <td style={tdStyle}>{row.idx + 1}</td>
                  <td style={{ ...tdStyle, color: colors.text, fontWeight: 600 }}>{row.name || <em style={{ color: colors.muted }}> - </em>}</td>
                  {isAccounts ? (
                    <>
                      <td style={tdStyle}>{row.type}</td>
                      <td style={{ ...tdStyle, fontFamily: fonts.mono }}>{fmt(row.balance)}</td>
                      <td style={tdStyle}>{row.is_emergency_fund ? <span style={{ color: colors.green }}>Yes</span> : 'No'}</td>
                      <td style={tdStyle}>{row.owner_label}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...tdStyle, fontFamily: fonts.mono }}>{fmt(row.amount)}</td>
                      <td style={tdStyle}>{row.due_day}</td>
                      <td style={tdStyle}>{row.category}</td>
                      <td style={tdStyle}>{row.frequency}{row.interval_months ? ` (${row.interval_months}mo)` : ''}</td>
                      <td style={tdStyle}>{row.owner_label || ' - '}</td>
                    </>
                  )}
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <ValidationBadge valid={row.valid} errorCount={row.errors.length} />
                      {row.errors.map((e, i) => (
                        <span key={i} style={{ fontSize: '16px', color: colors.red, fontFamily: fonts.body }}>{e}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <GhostButton onClick={() => { setStep(2); setParsed([]); setFileType(null) }}>← Re-upload</GhostButton>
          <GoldButton onClick={handleImport} disabled={validRows.length === 0 || importing}>
            {importing ? 'Importing...' : `Import ${validRows.length} ${fileType} →`}
          </GoldButton>
        </div>
      </div>
    )
  }

  // ── Step 4: Done ───────────────────────────────────────────────────────────

  function renderDone() {
    return (
      <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
        <div style={{ textAlign: 'center', padding: '20px 0 12px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
          <div style={{ fontFamily: fonts.heading, fontSize: '32px', fontWeight: 700, color: colors.gold, letterSpacing: '0.06em', marginBottom: '8px' }}>
            Import complete
          </div>
          <p style={{ fontSize: '16px', color: colors.muted, fontFamily: fonts.body, lineHeight: 1.7, marginBottom: '8px' }}>
            {importResult.imported} {fileType} imported successfully.
            {importResult.skipped > 0 && ` ${importResult.skipped} row${importResult.skipped !== 1 ? 's' : ''} were skipped due to errors.`}
          </p>
          {importResult.skipped > 0 && (
            <p style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: '24px' }}>
              To import the skipped rows, fix them in your CSV and run another import.
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
          <GhostButton onClick={() => {
            setStep(1); setFileType(null); setParsed([]); setParseError('')
          }}>
            Import another file
          </GhostButton>
          {onNavigate && (
            <GoldButton onClick={() => onNavigate(fileType === 'accounts' ? 'budget' : 'bills')}>
              Go to {fileType === 'accounts' ? 'Accounts' : 'Bills'} →
            </GoldButton>
          )}
        </div>
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ paddingBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '18px 20px 4px', position: 'relative' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text }}>Import</div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Bulk import accounts or bills from a CSV template</div>
        </div>
      </div>
      <div style={{ padding: '16px', maxWidth: '800px' }}>
        <StepIndicator current={step} />
        {step === 1 && renderTemplates()}
        {step === 2 && renderUpload()}
        {step === 3 && renderPreview()}
        {step === 4 && renderDone()}
      </div>
    </div>
  )
}

// ─── Table styles ─────────────────────────────────────────────────────────────

const thStyle = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.01em',
  color: colors.muted,
  whiteSpace: 'nowrap',
}

const tdStyle = {
  padding: '8px 10px',
  fontSize: '14px',
  color: colors.muted,
  verticalAlign: 'top',
}
